import { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { supabase } from '@/integrations/supabase/client'
import { useSafeNavigate } from '@/hooks/useSafeNavigate'
import { decideRoute } from '@/routing/decideRoute'
import { clearActing } from '@/lib/acting'

function readRoleFromSession(): 'employer' | 'candidate' | undefined {
  try {
  const r = ((): 'employer' | 'job_seeker' | null => {
    try {
      const v = sessionStorage.getItem('login_intent_role');
      return v === 'employer' || v === 'job_seeker' ? v : null;
    } catch { return null; }
  })() as any
    // Map job_seeker to candidate for compatibility
    if (r === 'job_seeker') return 'candidate';
    // Map candidate to job_seeker for DB consistency
    if (r === 'candidate') return 'candidate';
    return r === 'employer' ? r : undefined
  } catch { return undefined }
}


/**
 * PostAuth - Final OAuth routing decision maker
 * 
 * This component calls the post-auth-route Edge Function to:
 * 1. Verify the user's JWT
 * 2. Create/update their profile with the correct role
 * 3. Determine the next route based on their status
 * 
 * No complex state management - just a simple API call and redirect.
 */
export default function PostAuth() {
  const safeNavigate = useSafeNavigate()
  const location = useLocation()
  const didProcess = useRef(false)

  useEffect(() => {
    // Only run once per mount to prevent loops
    if (didProcess.current) return;
    didProcess.current = true;

    (async () => {
      try {
        // 0) Ensure we actually HAVE a session (many failures were from running too early)
        let tries = 0, session = null;
        while (tries < 10 && !session) {
          const { data } = await supabase.auth.getSession();
          session = data.session;
          if (!session) {
            await new Promise(r => setTimeout(r, 150));
            tries++;
          }
        }
        if (!session) {
          console.error('[post-auth] no session; redirecting to role-selection');
          safeNavigate('/role-selection?source=oauth&err=no_session');
          return;
        }

        // 1) Clear any lingering acting context from previous admin sessions
        clearActing();
        
        // 2) Optional header fallback for role (cookie may be blocked in dev)
        const urlRole = new URLSearchParams(location.search).get('role');
        let hdrRole = urlRole === 'employer' || urlRole === 'candidate' ? urlRole : readRoleFromSession();
        
        // 3) If no role hint, try to infer from referrer or current path
        if (!hdrRole) {
          const referrer = document.referrer;
          if (referrer.includes('/employer/') || referrer.includes('employer')) {
            hdrRole = 'employer';
            console.log('[PostAuth] Inferred employer role from referrer:', referrer);
          } else if (referrer.includes('/job-seeker/') || referrer.includes('job-seeker') || referrer.includes('/candidate/')) {
            hdrRole = 'candidate';
            console.log('[PostAuth] Inferred candidate role from referrer:', referrer);
          }
        }

        if (hdrRole) console.debug('[post-auth] sending x-role-hint:', hdrRole);
        console.log('🔧 PostAuth: Edge Function invocation details:', {
          hasRoleHint: !!hdrRole,
          roleHint: hdrRole,
          sessionRole: readRoleFromSession(),
          urlRole: new URLSearchParams(location.search).get('role'),
          sessionStorage: sessionStorage.getItem('login_intent_role')
        });

        // 2) Call the Edge Function
        const { data, error } = await supabase.functions.invoke('post-auth-route', {
          headers: { 'x-role-hint': hdrRole ?? '' }, // 'employer' | 'candidate'
          body: {}, // anything you want
        });

        console.log('🔧 PostAuth: Edge Function response:', {
          data,
          error,
          domain: window.location.hostname,
          hdrRole
        });
        
        // Additional debugging for routing
        console.log('🔧 PostAuth: Profile data from Edge Function:', {
          role: data?.role,
          onboarding_completed: data?.onboarding_completed,
          next: data?.next
        });

        if (error) {
          const code = (error as any).name || (error as any).code || (error as any).status;
          console.error('[post-auth] function error:', error);
          if (code === 'ROLE_HINT_MISSING' || (error as any).status === 400) {
            safeNavigate('/role-selection?source=oauth');
            return;
          }
          if ((error as any).status === 401) {
            safeNavigate('/role-selection?source=oauth&err=unauthorized');
            return;
          }
          safeNavigate('/role-selection?source=oauth&err=function');
          return;
        }

        if (!data?.next) {
          console.error('[post-auth] no next returned');
          safeNavigate('/role-selection?source=oauth&err=no_next');
          return;
        }

        // 3) Handle role intent and update profile if needed
        const roleHint = hdrRole as 'employer' | 'candidate' | undefined;
        
        if (roleHint === 'employer') {
          // Fetch current profile to check if role needs updating
          const { data: profile } = await supabase
            .from('profiles')
            .select('user_id, role')
            .eq('user_id', session.user.id)
            .maybeSingle();
          
          if (profile && profile.role !== 'employer') {
            console.log('[PostAuth] Updating profile role to employer');
            // Update profile role to employer
            await supabase.from('profiles')
              .update({ role: 'employer' })
              .eq('user_id', session.user.id);
            
            // Ensure employer_profiles stub exists
            await supabase
              .from('employer_profiles')
              .upsert({ user_id: session.user.id });
          }
        }

        // 4) Clean up hint + go
        try { sessionStorage.removeItem('login_intent_role') } catch {}
        
        // Use the computed landing from the Edge Function, or fallback to our unified logic
        let nextRoute = data.next;
        if (!nextRoute) {
          // Fetch profile data and use decideRoute
          const { data: profile } = await supabase
            .from('profiles')
            .select('user_id, role, onboarding_completed, verification_status')
            .eq('user_id', session.user.id)
            .maybeSingle();
          
          const { data: isAdmin } = await supabase.rpc('is_admin');
          
          if (profile) {
            // Map job_seeker to candidate for compatibility, but use job_seeker for routing
            const role = profile.role === 'candidate' ? 'job_seeker' : profile.role;
            const isAdminFlag = isAdmin || profile.role === 'admin';
            nextRoute = decideRoute(
              isAdminFlag ? 'admin' : role, 
              profile.onboarding_completed, 
              profile.verification_status
            );
          } else {
            nextRoute = '/role-selection';
          }
        }
        safeNavigate(nextRoute);
        
      } catch (e) {
        console.error('[post-auth] crash:', e);
        safeNavigate('/role-selection?source=oauth&err=crash');
      }
    })();
  }, []); // Remove location.search dependency to prevent loops

  // Render nothing - this is just a processing hop
  return null
}


